import first from 'lodash/first'
import split from 'lodash/split'
import { useRouter } from 'next/router'
import { Dispatch, SetStateAction, useCallback, useEffect, useRef, useState } from 'react'

import { ampli } from '@Analytics/ampli'
import { CheckoutFlowNavigationStep, CheckoutFlowStep, CheckoutFlowSteps } from '@Domains/CheckoutV2/Features/CheckoutSteps/Contexts/typings/CheckoutFlowTypes'

interface GenericStep {
  key: string
}

export const useCheckoutFlowNavigation = (
  activeStep: GenericStep,
  updateActiveStep: Dispatch<SetStateAction<GenericStep>>,
  stepValidations: Record<string, boolean>,
  productReturnsPageStepList: GenericStep[],
  initValidation: Record<CheckoutFlowStep, boolean>,
) => {
  const ref = useRef(false)
  const [init, setInit] = useState(true)
  const [hasHadError, setHasHadError] = useState(false)
  const [failedNavigation, setFailedNavigation] = useState<string | null>(null)
  const [failedNavigationAttempts, setFailedNavigationAttempts] = useState<number>(0)
  const {
    asPath,
    locale,
    pathname,
    push,
    query,
    replace,
  } = useRouter()
  const firstInList: GenericStep = first(productReturnsPageStepList) || CheckoutFlowSteps.ADDRESS

  const allPreviousStepsValid = useCallback((step?: CheckoutFlowNavigationStep) => {
    if (!step) {
      return false
    }
    if (step === firstInList || stepValidations[step.key]) {
      return true
    }
    const firstInvalidIndex = productReturnsPageStepList.findIndex(item => !stepValidations[item.key])
    return step.key === productReturnsPageStepList[firstInvalidIndex].key
  }, [firstInList, productReturnsPageStepList, stepValidations])

  const findFirstPossibleStep: (step?: CheckoutFlowNavigationStep) => GenericStep = useCallback(
    (step?: CheckoutFlowNavigationStep) => {
      if (!step) {
        return firstInList
      }

      return productReturnsPageStepList.find(item => !stepValidations[item.key]) || firstInList
    },
    [firstInList, productReturnsPageStepList, stepValidations],
  )

  const getQueryPathName = useCallback((key: string) => {
    switch (key) {
      case CheckoutFlowStep.ADDRESS:
        return CheckoutFlowStep.ADDRESS.toLowerCase()
      case CheckoutFlowStep.SHIPPING:
        return 'shipping'
      default:
        return CheckoutFlowStep.ADDRESS.toLowerCase()
    }
  }, [])

  const goToStep = useCallback((stepKey?: string, active: boolean = false) => {
    if (activeStep.key === CheckoutFlowSteps.SUCCESS.key || stepKey === CheckoutFlowSteps.SUCCESS.key) {
      if (active) {
        setFailedNavigation(activeStep.key)
        setHasHadError(true)
        setFailedNavigationAttempts(prev => prev + 1)
      }
      return
    }
    const updateFailed = () => {
      if (active) {
        setFailedNavigation(activeStep.key)
        setHasHadError(true)
        setFailedNavigationAttempts(prev => prev + 1)
      }
    }
    if (ref.current === true) {
      setFailedNavigationAttempts(prev => prev + 1)
      return
    }
    if (!stepKey || stepKey === activeStep.key) {
      updateFailed()
      return
    }
    ref.current = true
    const currentStep = CheckoutFlowSteps[stepKey]
    const isStepValid = allPreviousStepsValid(currentStep)
    if (isStepValid) {
      setFailedNavigation(null)
      setFailedNavigationAttempts(0)
    }
    if (!currentStep || !isStepValid) {
      const previousValidStep: GenericStep = findFirstPossibleStep(currentStep)
      if (activeStep.key === previousValidStep.key) {
        updateFailed()
      }
      updateActiveStep(prev => (prev.key === previousValidStep.key ? prev : previousValidStep))
      replace(
        {
          pathname,
          query: getQueryPathName(previousValidStep!.key),
        },
        {
          pathname: split(asPath, '?')[0],
          query: getQueryPathName(previousValidStep!.key),
        },
        {
          locale,
          shallow: true,
          scroll: false,
        },
      )
        .then(() => {
          ref.current = false
        })
      return
    }
    if (isStepValid) {
      updateActiveStep(prev => (prev.key === currentStep.key ? prev : currentStep))
      push(
        {
          pathname,
          query: getQueryPathName(currentStep.key),
        },
        {
          pathname: split(asPath, '?')[0],
          query: getQueryPathName(currentStep.key),
        },
        {
          locale,
          shallow: true,
          scroll: false,
        },
      )
        .then(() => {
          ref.current = false
          window.scrollTo({
            top: 0,
            behavior: 'smooth',
          })
        })
      return
    }
  }, [activeStep.key, allPreviousStepsValid, replace, getQueryPathName, asPath, locale, findFirstPossibleStep, updateActiveStep, push, pathname])

  useEffect(() => {
    const stepKey: string | undefined = Object.keys(query)
      .find(queryKey => !!CheckoutFlowSteps[queryKey.toUpperCase()])
      ?.toUpperCase()
    if (init) {
      setInit(false)
      replace(
        {
          pathname,
          query: stepKey?.toLowerCase() || 'address',
        },
        {
          pathname: split(asPath, '?')[0],
          query: stepKey?.toLowerCase() || 'address',
        },
        {
          locale,
          shallow: true,
          scroll: false,
        },
      )
        .then(() => {
          ref.current = false
          window.scrollTo({
            top: 0,
            behavior: 'smooth',
          })
        })
    }
    if (stepKey) {
      ampli.eCommerceCheckout({
        cof_step_no: stepKey?.toUpperCase() === CheckoutFlowStep.ADDRESS ? 1 : 2,
      })
    }
    if (stepKey === CheckoutFlowStep.SHIPPING && initValidation.ADDRESS) {
      const currentStep = CheckoutFlowSteps[stepKey]
      updateActiveStep(prev => (prev.key === currentStep.key ? prev : currentStep))
      return
    }
    if (!stepKey) {
      const currentStep = CheckoutFlowSteps[CheckoutFlowStep.ADDRESS]
      updateActiveStep(currentStep)
      replace(
        {
          pathname,
          query: 'address',
        },
        {
          pathname: split(asPath, '?')[0],
          query: 'address',
        },
        {
          locale,
          shallow: true,
          scroll: false,
        },
      )
        .then(() => {
          ref.current = false
          window.scrollTo({
            top: 0,
            behavior: 'smooth',
          })
        })
      return
    }
    if (stepKey) {
      const currentStep = CheckoutFlowSteps[CheckoutFlowStep.ADDRESS]
      updateActiveStep(currentStep)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query])

  return {
    failedNavigation,
    failedNavigationAttempts,
    hasHadError,
    setActiveStep: (step: CheckoutFlowNavigationStep) => goToStep(step.key, true),
  }
}
